/**
 * Modèles d'authentification (utilisateur, entreprise, droits).
 *
 * Note : ces types sont également ré-exportés depuis `auth.service.ts`
 * pour préserver les imports historiques. Pour tout nouveau code, importer
 * depuis `@core/auth/user.model`.
 */

export interface Entreprise {
  id: number;
  code: string;
  nom: string;
  estActive: boolean;
  dateCreation: Date;
}

export interface Societe {
  id: number;
  code?: string;
  nom: string;
}

export interface User {
  id: number;
  login: string;
  name: string;
  firstName?: string;
  lastName?: string;
  employeId?: number;
  /** Identifiants société/agence rattachés à l'employé connecté (renvoyés par le backend pour pré-remplir les formulaires). */
  employeAgenceId?: number;
  employeSocieteId?: number;

  /**
   * Champs FR rempli par le backend (ancien format) — gardés optionnels pour
   * compat. Préférer `firstName`/`lastName` quand disponibles.
   */
  prenom?: string;
  nom?: string;
  email?: string;

  // Droits d'accès aux widgets
  accesRechercheArticles: boolean;
  accesAssistantIngenieur: boolean;
  accesCreationCourrier: boolean;
  accesAssistantReunion: boolean;
  accesAssistantJuridiqueRH: boolean;
  accesAssistantSSI: boolean;
  accesPlansFabrication: boolean;
  accesAnalyseDCE: boolean;
  accesAnalyseAppelOffre: boolean;
  accesRapportSSE: boolean;
  accesEquipementHauteur: boolean;
  accesVerificationsVehicules: boolean;
  accesAssistantMagasinier: boolean;
  accesCauserie: boolean;
  accesAnomalie: boolean;
  /**
   * Droit pour le widget "Mise en service / Vérification PV".
   * Optionnel : peut ne pas être renvoyé par les anciennes versions du backend.
   */
  accesMiseEnService?: boolean;
  /**
   * Droit pour l'écran "Analyseur de plans" (pré-métré depuis DWG).
   * Optionnel : peut ne pas être renvoyé par les anciennes versions du backend.
   */
  accesAnalyseurPlans?: boolean;
  /**
   * Droit pour l'écran "Appels d'offres MOE" (veille sur les DCE).
   * Optionnel : peut ne pas être renvoyé par les anciennes versions du backend.
   */
  accesAppelOffresMoe?: boolean;
  /** Droit pour le « Comparateur de mémoire technique » (cohérence MT / DPGF / fiches d'une offre). */
  accesComparateurMemoireTechnique?: boolean;

  /** Droit pour le comparateur de prix (offres d'entreprises sur DPGF). */
  accesComparateurPrix?: boolean;
  accesAccueilSSE?: boolean;
  /** Accueil SSE en lecture seule : voir qui a été accueilli, sans réaliser d'accueil. */
  accesAccueilSSELecture?: boolean;
  accesPlanActionSSE?: boolean;
  accesPlanActionsImmediates?: boolean;
  accesGestionFds?: boolean;
  /** Rapports d'intervention : modifier, archiver et supprimer tous les rapports (sociétés du périmètre). */
  accesModificationTousRapports?: boolean;

  // Droits Tableaux de bord
  accesTbChantierSSE: boolean;
  accesTbEquipementHauteur: boolean;
  accesTbVehicule: boolean;
  recoitRecapHebdoSse: boolean;
  accesTbCauserie: boolean;
  accesTbAnomalie: boolean;

  // Droits d'administration
  estAdministrateur: boolean;
  accesGestionCatalogues: boolean;
  accesGestionDocuments: boolean;
  accesGestionUtilisateurs: boolean;
  accesCreationModificationEquipement: boolean;
  accesCreationModificationVehicule: boolean;

  // Droits d'accès aux entreprises
  entreprises?: Entreprise[];
  entrepriseCodes?: string[];
  societeIds?: number[];
  societes?: Societe[];

  // Double authentification
  twoFactorEnabled?: boolean;
}

/**
 * Union de toutes les clés de droits booléens présentes sur `User`.
 * Permet une vérification typée plutôt qu'un accès dynamique `user[string]`.
 */
export type UserRight =
  | 'accesRechercheArticles'
  | 'accesAssistantIngenieur'
  | 'accesCreationCourrier'
  | 'accesAssistantReunion'
  | 'accesAssistantJuridiqueRH'
  | 'accesAssistantSSI'
  | 'accesPlansFabrication'
  | 'accesAnalyseDCE'
  | 'accesAnalyseAppelOffre'
  | 'accesRapportSSE'
  | 'accesEquipementHauteur'
  | 'accesVerificationsVehicules'
  | 'accesAssistantMagasinier'
  | 'accesCauserie'
  | 'accesAnomalie'
  | 'accesMiseEnService'
  | 'accesAnalyseurPlans'
  | 'accesAppelOffresMoe'
  | 'accesComparateurMemoireTechnique'
  | 'accesComparateurPrix'
  | 'accesAccueilSSE'
  | 'accesAccueilSSELecture'
  | 'accesPlanActionSSE'
  | 'accesPlanActionsImmediates'
  | 'accesGestionFds'
  | 'accesModificationTousRapports'
  | 'accesTbChantierSSE'
  | 'accesTbEquipementHauteur'
  | 'accesTbVehicule'
  | 'recoitRecapHebdoSse'
  | 'accesTbCauserie'
  | 'accesTbAnomalie'
  | 'estAdministrateur'
  | 'accesGestionCatalogues'
  | 'accesGestionDocuments'
  | 'accesGestionUtilisateurs'
  | 'accesCreationModificationEquipement'
  | 'accesCreationModificationVehicule';

/**
 * Vérifie qu'un utilisateur possède un droit donné.
 * Renvoie `false` si l'utilisateur est null ou si le droit n'est pas présent.
 *
 * Usage typé :
 *   if (hasUserRight(user, 'accesGestionUtilisateurs')) { ... }
 *
 * Pour un accès depuis une chaîne dynamique (ex : un mapping menu), caster
 * explicitement : `hasUserRight(user, key as UserRight)`.
 */
export function hasUserRight(user: User | null | undefined, right: UserRight): boolean {
  if (!user) return false;
  return user[right] === true;
}

/**
 * Variante : vérifie qu'au moins un des droits passés est accordé.
 */
export function hasAnyUserRight(user: User | null | undefined, rights: readonly UserRight[]): boolean {
  if (!user) return false;
  return rights.some(r => user[r] === true);
}
